import { createNewDate } from '@mixins/moment-mixin';
import Route, { ROUTE_TYPE_DELIVERY } from '@models/route';
import sortBy from 'lodash/sortBy';

export default {
    namespaced: true,
    state: {
        date: createNewDate(),
        routes: [],
        selectedRoutes: [],
        filterByStatus: null,
        filterByType: null,
        filterByBranch: null,
        filterByVehicleType: null,
        filterByDiTechnician: null,
    },
    getters: {
        filteredRoutes(state) {
            let { routes } = state;
            if (state.filterByBranch) {
                routes = routes.filter((route) => route.starting_branch_id === state.filterByBranch);
            }

            if (state.filterByVehicleType) {
                routes = routes.filter((route) => route.vehicle?.vehicle_type_id === state.filterByVehicleType);
            }

            if (state.filterByStatus) {
                routes = routes.filter((route) => route.status === state.filterByStatus);
            }

            if (state.filterByType) {
                routes = routes.filter((route) => (state.filterByType === 'Dynamic' ? !route.dms_driver_code_id : route.dms_driver_code_id));
            }

            let dynamicRoutes = routes.filter((r) => !r.isStatic());
            dynamicRoutes = sortBy(dynamicRoutes, (d) => (d.vehicle_id ? d.vehicle.name : false));
            const staticRoutes = routes.filter((r) => r.isStatic());

            if (state.filterByType === 'Dynamic') {
                return dynamicRoutes;
            }
            if (state.filterByType === 'Static') {
                return staticRoutes;
            }
            return [...dynamicRoutes, ...staticRoutes];
        },
        routeId(state) {
            return state.date.format('Y-MM-DD');
        },
    },
    mutations: {
        setRoutes(state, routes) {
            state.routes = routes;
        },
        setDate(state, date) {
            state.date = date;
        },
        setSelectedRoutes(state, routes) {
            state.selectedRoutes = routes;
        },
        setFilterBranch(state, branchId) {
            state.filterByBranch = branchId;
        },
        setFilterVehicleType(state, status) {
            state.filterByVehicleType = status;
        },
        setFilterStatus(state, status) {
            state.filterByStatus = status;
        },
        setFilterType(state, type) {
            state.filterByType = type;
        },
        updateRoute(state, route) {
            const routeIndex = state.routes.findIndex((r) => r.id === route.id);

            if (routeIndex !== -1) {
                state.routes.splice(routeIndex, 1, route);
            } else {
                state.routes.push(route);
            }
        },
        setRouteAsPendingOptimizing(state, { routeId }) {
            const route = state.routes.find((r) => r.id === Number(routeId));

            if (route) {
                route.pendingOptimizing = true;
            }
        },
        setRouteAsOptimizing(state, routeId) {
            const route = state.routes.find((r) => r.id === Number(routeId));

            if (route) {
                route.optimizing = true;
            }
        },
        removeRoute(state, routeId) {
            const routeIndex = state.routes.findIndex((r) => r.id === routeId);

            if (routeIndex !== -1) {
                state.routes.splice(routeIndex, 1);
            }
        },
    },
    actions: {
        async setDate({ commit }, date) {
            commit('setDate', date);
        },
        async getRoutesByDate({ commit, getters }) {
            const routes = await new Route()
                .where('date', getters.routeId)
                .where('type', ROUTE_TYPE_DELIVERY)
                .with('branch', 'user', 'vehicle', 'tasks', 'codriver')
                .query('include_polylines', true)
                .orderBy('vehicle.name')
                .get();

            commit('setRoutes', routes);
        },
        setSelectedRoutes({ commit }, routes) {
            commit('setSelectedRoutes', routes);
        },
        filterByBranch({ commit }, branchId) {
            commit('setFilterBranch', branchId);
        },
        filterByVehicleType({ commit }, status) {
            commit('setFilterVehicleType', status);
        },
        filterByStatus({ commit }, status) {
            commit('setFilterStatus', status);
        },
        filterByType({ commit }, type) {
            commit('setFilterType', type);
        },
        resetFilters({ commit }) {
            commit('setFilterBranch', null);
            commit('setFilterVehicleType', null);
            commit('setFilterStatus', null);
            commit('setFilterType', null);
        },
        resetStatusFilter({ commit }) {
            commit('setFilterStatus', null);
        },
        resetVehicleTypeFilter({ commit }) {
            commit('setFilterVehicleType', null);
        },
        resetBranchFilter({ commit }) {
            commit('setFilterBranch', null);
        },
        async refresh({ dispatch }) {
            await dispatch('getRoutesByDate');
        },
        async refreshRoute({ commit }, routeId) {
            const route = await Route
                .with('branch', 'user', 'vehicle', 'tasks', 'codriver')
                .query('include_polylines', true)
                .find(routeId);

            commit('updateRoute', route);
        },
    },
};
